/**
 * @file actions.ts
 * @description Центральный API модуль для Task Manager приложения, который обеспечивает унифицированный интерфейс для выполнения HTTP-запросов через Axios с специальной обработкой пустых GUID идентификаторов. Включает функции для всех типов HTTP операций (GET, POST, PUT, DELETE), поддержку загрузки файлов, обработку blob-данных и автоматическое удаление пустых VITE_EMPTY_GUID идентификаторов из параметров запросов. Также экспортирует специализированные API модули для каталогов, перечислений и форм объектов.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-15
 * @dependencies ['axios', './config', '../types', './actions/catalog', './actions/enum', './actions/object-form']
 * @related-files ['src/api/config.ts', 'src/api/actions/catalog.ts', 'src/api/actions/enum.ts', 'src/api/actions/object-form.ts', 'src/types/t-object.ts']
 */

import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import instance from './config';
import { TApiData } from '../types';

export * from './actions/catalog';
export * from './actions/enum';
export * from './actions/object-form';

/**
 * Отправляет GET-запрос по указанной конечной точке (endpoint) с заданными параметрами.
 *
 * @param {string} entryPoint - URL конечной точки API.
 * @param {TApiData} params - Объект с параметрами запроса.
 * @returns {Promise<AxiosResponse | AxiosError>} - Промис, который разрешается в данные ответа или ошибку.
 */
export const getRequest = async (entryPoint: string, params?: TApiData): Promise<AxiosResponse | AxiosError> => {
    if (params?.id && params.id === import.meta.env.VITE_EMPTY_GUID) {
        delete params.id;
    }

    return await instance()
        .get(entryPoint, { params })
        .then((response: AxiosResponse) => response)
        .catch((error: AxiosError) => error);
};

/**
 * Отправляет DELETE-запрос по указанной конечной точке с заданными параметрами.
 *
 * @param {string} entryPoint - URL конечной точки API.
 * @param {TApiData} [params={}] - Объект с параметрами запроса.
 * @returns {Promise<AxiosResponse | AxiosError>} - Промис, который разрешается в данные ответа или ошибку.
 */
export const deleteRequest = async (entryPoint: string, params?: TApiData): Promise<AxiosResponse | AxiosError> => {
    if (params?.id && params.id === import.meta.env.VITE_EMPTY_GUID) {
        delete params.id;
    }

    return await instance()
        .delete(entryPoint, { params })
        .then((response: AxiosResponse) => response)
        .catch((error: AxiosError) => error);
};

/**
 * Отправляет GET-запрос для получения файла (blob) по указанной конечной точке с заданными параметрами.
 *
 * @param {string} entryPoint - URL конечной точки API.
 * @param {TApiData} [params={}] - Объект с параметрами запроса.
 * @returns {Promise<AxiosResponse | AxiosError>} - Промис, который разрешается в данные файла (blob) или ошибку.
 */
export const getFile = async (entryPoint: string, params?: TApiData): Promise<AxiosResponse | AxiosError> => {
    if (params?.id && params.id === import.meta.env.VITE_EMPTY_GUID) {
        delete params.id;
    }

    return await instance({ responseType: 'blob' })
        .get(entryPoint, { params })
        .then((response: AxiosResponse) => response)
        .catch((error: AxiosError) => error);
};

/**
 * Отправляет POST-запрос для загрузки файла по указанной конечной точке.
 *
 * @param {string} entryPoint - URL конечной точки API.
 * @param {TApiData} body - Тело запроса.
 * @param {TApiData} params - Объект с параметрами запроса.
 * @returns {Promise<AxiosResponse | AxiosError>} - Промис, который разрешается в данные ответа или ошибку.
 */
export const postFile = async (
    entryPoint: string,
    body?: TApiData,
    params?: TApiData
): Promise<AxiosResponse | AxiosError> => {
    if (params?.id && params.id === import.meta.env.VITE_EMPTY_GUID) {
        delete params.id;
    }

    return await instance({ responseType: 'blob' })
        .post(entryPoint, body, params)
        .then((response: AxiosResponse) => response)
        .catch((error: AxiosError) => error);
};

/**
 * Отправляет универсальный POST-запрос по указанной конечной точке.
 * Если id в параметрах или теле запроса равен VITE_EMPTY_GUID, то удаляет его.
 *
 * @param {string} entryPoint - URL конечной точки API.
 * @param {TApiData} postBody - Тело запроса.
 * @param {TApiData} params - Объект с параметрами запроса.
 * @param {*} [extra=undefined] - Дополнительные настройки для запроса.
 * @returns {Promise<AxiosResponse | AxiosError>} - Промис, который разрешается в ответ или ошибку.
 */
export const postRequest = async (
    entryPoint: string,
    postBody?: TApiData,
    params?: TApiData,
    extra?: AxiosRequestConfig
): Promise<AxiosResponse | AxiosError> => {
    if (params?.id && params.id === import.meta.env.VITE_EMPTY_GUID) {
        delete params.id;
    }

    if (postBody?.id && postBody.id === import.meta.env.VITE_EMPTY_GUID) {
        delete postBody.id;
    }

    return await instance(extra)
        .post(entryPoint, postBody, { params })
        .then((response: AxiosResponse) => response)
        .catch((error: AxiosError) => error);
};

/**
 * Отправляет PUT-запрос для обновления ресурса по указанной конечной точке.
 * Если id в параметрах или теле запроса равен VITE_EMPTY_GUID, то удаляет его.
 *
 * @param {string} entryPoint - URL конечной точки API.
 * @param {TApiData} postBody - Тело запроса.
 * @param {TApiData} params - Объект с параметрами запроса.
 * @returns {Promise<AxiosResponse | AxiosError>} - Промис, который разрешается в ответ или ошибку.
 */
export const putRequest = async (
    entryPoint: string,
    postBody?: TApiData,
    params?: TApiData
): Promise<AxiosResponse | AxiosError> => {
    if (params?.id && params.id === import.meta.env.VITE_EMPTY_GUID) {
        delete params.id;
    }

    if (postBody?.id && postBody.id === import.meta.env.VITE_EMPTY_GUID) {
        delete postBody.id;
    }

    return await instance()
        .put(entryPoint, postBody, { params })
        .then((response: AxiosResponse) => response)
        .catch((error: AxiosError) => error);
};
